
import { Users, Home, Mail, Shield, CreditCard, ArrowRight, CheckCircle, Star, MapPin, Clock } from "lucide-react";

const HowItWorks = () => {
  const steps = [
    {
      icon: Users,
      title: "Sign Up & Verify",
      description: "Register on the platform using your email or Google account. We verify your identity for added security and trust.",
      image: "/lovable-uploads/708ade69-9aec-4a4d-bb15-f709cc32cfc3.png",
      bgColor: "from-blue-500 to-blue-600",
      step: "01",
      highlight: "Account Setup"
    },
    {
      icon: Shield,
      title: "Connect with Verified Suppliers",
      description: "Browse through trusted builders, artisans, service providers, and suppliers already vetted by our community in Zimbabwe.",
      image: "/lovable-uploads/12d9a81a-913b-458e-80ad-3207f6519f13.png",
      bgColor: "from-green-500 to-green-600",
      step: "02",
      highlight: "Verified Network"
    },
    {
      icon: Home,
      title: "Start a Project",
      description: "Create a project (e.g., building a house or buying groceries) and assign a supplier or family contact in Zimbabwe.",
      image: "/lovable-uploads/efb649e7-3c86-43e9-bc55-ea80ccac0d00.png",
      bgColor: "from-orange-500 to-orange-600",
      step: "03",
      highlight: "Project Creation"
    },
    {
      icon: Mail,
      title: "Track Progress in Real-Time",
      description: "Receive photo, video, or document updates linked to milestones you set — with automatic timestamps and notes from your agents.",
      image: "/lovable-uploads/e878ad1f-98df-41aa-ae9e-44450205ed2f.png",
      bgColor: "from-purple-500 to-purple-600",
      step: "04",
      highlight: "Live Updates"
    },
    {
      icon: CreditCard,
      title: "Peace of Mind",
      description: "Know exactly where your money is going. Trust, transparency, and traceability for the diaspora community worldwide.",
      image: "/lovable-uploads/3f918b0b-55b8-4bf0-8d01-4c0f3fa6c662.png",
      bgColor: "from-indigo-500 to-indigo-600",
      step: "05",
      highlight: "Complete Trust"
    }
  ];

  const trustBadges = [
    {
      icon: Shield,
      title: "Verified Suppliers",
      description: "All suppliers vetted by community"
    },
    {
      icon: CreditCard,
      title: "Mukuru Payments",
      description: "Secure remittance integration"
    },
    {
      icon: CheckCircle,
      title: "Agent-Verified Photos",
      description: "Real-time project documentation"
    }
  ];

  const handleGetStarted = () => {
    const joinWaitlistSection = document.getElementById('join-waitlist');
    if (joinWaitlistSection) {
      joinWaitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="how-it-works" className="py-20 sm:py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-orange-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse" style={{animationDelay: '2s'}}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <CheckCircle className="w-4 h-4" />
            Getting Started
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 px-2">
            How ZimKonnect
            <span className="block bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Works for You
            </span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 max-w-3xl mx-auto px-2 leading-relaxed">
            A simple, transparent process that connects Zimbabwe diaspora communities 
            with their building investments and family projects back home
          </p>
        </div>

        {/* Steps Section */}
        <div className="space-y-16 lg:space-y-24 mb-20">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-16 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Image Side */}
              <div className="flex-1 relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-50 to-gray-100 p-8">
                  <img
                    src={step.image}
                    alt={`Step ${step.step}: ${step.title} - ZimKonnect diaspora project tracking illustration`}
                    className="w-full h-80 sm:h-96 object-contain"
                    loading="lazy"
                  />
                  {/* Step badge overlay */}
                  <div className="absolute top-4 left-4 w-12 h-12 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-2xl flex items-center justify-center font-bold text-lg shadow-lg">
                    {step.step}
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
                  {step.highlight}
                </div>

                <div className={`w-16 h-16 bg-gradient-to-r ${step.bgColor} rounded-2xl flex items-center justify-center mb-6 mx-auto lg:mx-0 shadow-lg`}>
                  <step.icon className="text-white" size={28} aria-hidden="true" />
                </div>

                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  {step.title}
                </h2>
                
                <p className="text-gray-600 leading-relaxed text-lg mb-8 max-w-md mx-auto lg:mx-0">
                  {step.description}
                </p>

                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  aria-label={`Get started with step ${step.step}: ${step.title}`}
                >
                  <span>Get Started</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Section */}
        <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-3xl p-8 sm:p-12 mb-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why Diaspora Families Trust ZimKonnect
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built by diaspora, for diaspora — with the security and transparency you deserve
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustBadges.map((badge, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <badge.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {badge.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {badge.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials Preview */}
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 mb-16 border border-gray-100">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              What Diaspora Families Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "Finally, I can see exactly what's happening with my house build in Harare. The photos and updates give me such peace of mind."
              </p>
              <div className="flex items-center justify-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  L
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Leah M.</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Leeds, UK 🇬🇧
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "The supplier verification saved me thousands. I know I'm working with trusted people now."
              </p>
              <div className="flex items-center justify-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  M
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">Memory T.</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Toronto, CA 🇨🇦
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center md:col-span-2 lg:col-span-1">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "Real-time updates from my farm project. I can see every bag of fertilizer, every repair. Amazing transparency!"
              </p>
              <div className="flex items-center justify-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-semibold">
                  J
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">James S.</p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Sydney, AU 🇦🇺
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Call to Action */}
        <div className="text-center bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 sm:p-12 text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to Start Tracking Your Zimbabwe Project?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of diaspora families who trust ZimKonnect to monitor their investments back home. 
            Every dollar tracked, every milestone documented.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-blue-600 to-orange-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-blue-700 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center justify-center gap-2"
            >
              <span>Get Started Now</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                const liveDemo = document.getElementById('live-demo');
                if (liveDemo) {
                  liveDemo.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Clock className="w-5 h-5" />
              <span>See Live Demo</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
